/**
 * Оболочка кабинета: шапка плюс место маршрута.
 *
 * Приложение монтирует **оболочку**, а не экран. Шапка — переключатель фирмы и
 * переключатель языка — создаётся один раз, и переходы сменяют только
 * содержимое места маршрута: пересобранная на каждом переходе шапка означала
 * бы, что выбранная фирма живёт в перерисовке, а не в адресе.
 *
 * Переключатель заполняется из профиля и **только** из него: имя фирмы
 * приезжает вложенной записью (`membership.company.name`), второго обращения за
 * именем нет. Из чего складывается действующее участие, решает сервер (решение
 * `D129`), и копия этого условия здесь разошлась бы с ним молча, показав
 * работающий интерфейс с чужими правами. Поэтому статус не разбирается: членство
 * с пустым `available_sections` значит «этой фирмы в кабинете нет» (`D565` п. 4,
 * блок `WEB-D.2`, решение `Б10`), и сравнение идёт только с длиной списка.
 *
 * Своей таблицы «роль → что можно» у оболочки нет и завести её нельзя: пункты
 * выбирает список сервера, а адрес фирмы, которой нет в профиле, локально не
 * отклоняется — он уходит на сервер, и на экран попадает ответ. Интерфейс не
 * граница безопасности.
 *
 * Смену адреса оболочка слушает **сама**: переход, оставшийся в адресной
 * строке и не дошедший до экрана, — это находка шага СТЕНД, а не мелочь.
 * Подписка одна на все причины перехода: переключатель, кнопка «назад»,
 * набранная руками ссылка и возврат с созданной фирмы приходят одним и тем же
 * событием, и второй путь перерисовки рядом означал бы, что переход по
 * переключателю живёт по своим правилам.
 *
 * Экраны логиста — блок `WEB-2.1`: «Мои объявления», форма объявления и
 * карточка с offer'ами и доской монтируются тем же местом маршрута, по форме
 * соседей.
 *
 * Экраны диспетчера — блок `WEB-2.2`: лента грузов, объявление ленты с формой
 * offer'а, «Мои предложения», работы фирмы и «ждут назначения», назначение.
 * Экран работ получает фирму из адреса (переворот spec §6); экраны с вводом —
 * лента, объявление, назначение — переподписываются при смене языка.
 *
 * Экраны механика — блок `WEB-2.4`: карточка машины и «Водители фирмы» тем же
 * местом маршрута. Карточка и экран сервиса несут формы ввода и потому
 * переподписываются при смене языка, а не пересобираются (долг `TD-72`).
 *
 * Экраны бухгалтера — блок `WEB-2.3`: «Расходы» и «Выплаты» тем же местом
 * маршрута; «Водители фирмы» — прежний экран блока `WEB-2.4`. Форма записи и
 * правки живёт на экране списка и переподписывается при смене языка.
 *
 * Язык (решение `D122`): гость выбирает на время вкладки, и выбор уходит
 * заголовком на обращениях без токена; у вошедшего решение принимает сервер, и
 * интерфейс встаёт на язык **из ответа** — и `PATCH /v1/me` при смене, и
 * `GET /v1/me` при входе. Разрешение из браузера остаётся началом жизни вкладки
 * и границей гостя. Хранилища выбор не получает — перезагрузка возвращает язык
 * браузера.
 *
 * Меню — блок `WEB-D.1`, решения `Б3`, `Б12`, `Б14`. Стоит при фирме в адресе; роль
 * и признаки членства здесь не читаются, запросов меню не стоит.
 *
 * ПЕРЕВОРОТ блока `WEB-D.2`, решение `Б9` (`FR-WEB-011`, `D565`). Было: набор один
 * для всех и на чужой фирме тот же. Стало: пункт показан, только если его раздел
 * есть в `available_sections` членства этой фирмы; «Профиль» раздела не имеет.
 * Профиль не загружен, фирма чужая или закрытая — в меню только «Профиль». Прямой
 * адрес раздела вне списка по-прежнему спрашивает сервер: поле — подсказка меню,
 * граница у сервера. Что прежний ловил, а новый нет: одинаковость меню всех ролей.
 *
 * «Выйти» — блок `WEB-D.2`, решение `Б16`: кнопка в шапке вошедшего, путь на вход
 * тот же, что у завершения текущего сеанса в профиле.
 */

import { ApiFailure } from './api/client';
import type { ApiClient } from './api/client';
import { membershipsIn } from './api/contract';
import type { CompanySection, CurrentUserData, Membership } from './api/contract';
import { SUPPORTED_LOCALES, activeLocale, resolveLocale, setLocale } from './i18n';
import type { Locale, Strings } from './i18n';
import {
  cargoFeedPath,
  cargoListingsPath,
  cargoOffersPath,
  companyPath,
  companyProfilePath,
  currentRoute,
  driversPath,
  expensesPath,
  fleetPath,
  listingIdOf,
  membersPath,
  membershipIdOf,
  navigate,
  onRouteChange,
  payoutsPath,
  profilePath,
  replaceRoute,
  vehicleIdOf,
  vinClaimsPath,
  workIdOf,
  worksPath,
  worksToAssignPath,
} from './router';
import type { Route } from './router';
import { createCargoFeedScreen } from './screens/cargo-feed';
import { createCargoFeedListingScreen } from './screens/cargo-feed-listing';
import { createCargoListingScreen } from './screens/cargo-listing';
import { createCargoListingFormScreen } from './screens/cargo-listing-form';
import { createCargoListingsScreen } from './screens/cargo-listings';
import { createCargoOffersScreen } from './screens/cargo-offers';
import { createCompanyScreen } from './screens/company';
import { createCompanyDriversScreen } from './screens/company-drivers';
import { createCompanyProfileScreen } from './screens/company-profile';
import { createExpensesScreen } from './screens/expenses';
import { createFleetScreen } from './screens/fleet';
import { createInternalWorkFormScreen } from './screens/internal-work-form';
import { createLoginScreen } from './screens/login';
import { createMemberDocumentsScreen } from './screens/member-documents';
import { createMemberRightsScreen } from './screens/member-rights';
import { createMembersScreen } from './screens/members';
import { createNoMembershipScreen } from './screens/no-membership';
import { createNotFoundScreen } from './screens/not-found';
import { createPayoutsScreen } from './screens/payouts';
import { createProfileScreen } from './screens/profile';
import { refusalText, renderScreenState, screenStateFor } from './screens/state';
import type { ScreenState } from './screens/state';
import { createVehicleCardScreen } from './screens/vehicle-card';
import { createVehicleServiceScreen } from './screens/vehicle-service';
import { createVinClaimsScreen } from './screens/vin-claims';
import { createWorkAssignmentScreen } from './screens/work-assignment';
import { createWorkCardScreen } from './screens/work-card';
import { createWorksScreen } from './screens/works';

export const ROOT_SELECTOR = '#app';

export interface ShellOptions {
  readonly client: ApiClient;
  /** Начальное значение языка. Без него — разрешение из браузера. */
  readonly locale?: Locale;
}

export interface Shell {
  readonly element: HTMLElement;
  readonly header: HTMLElement;
  readonly route: Route;
  readonly locale: Locale;
  /** Фирма из адреса либо `null` — адрес фирмы не называет. */
  readonly activeCompanyId: string | null;
  /** Фирмы профиля в том порядке, в котором их вернул сервер. */
  readonly companyIds: readonly string[];
  load(): Promise<void>;
  selectCompany(companyId: string): void;
  selectLocale(locale: Locale): Promise<void>;
}

/** Экран места маршрута: узел и, если он умеет грузиться, его загрузка. */
interface MountedScreen {
  readonly element: HTMLElement;
  readonly load: (() => Promise<void>) | null;
  /**
   * Переподписать экран на новом языке, не трогая введённого (долг `TD-72`).
   *
   * Есть только у экранов с формой ввода: им смена языка не пересобирает узел
   * — иначе введённое уходит вместе с пустым шаблоном, — а меняет подписи на
   * месте. Экраны без ввода этого не умеют и пересобираются, как прежде.
   */
  readonly relabel?: (next: Strings) => void;
  /** Снятие экрана: таймеры гаснут, поздние ответы не применяются (`TD-63`). */
  readonly dispose?: () => void;
}

export function requireRoot(doc: Document): HTMLElement {
  const root = doc.querySelector<HTMLElement>(ROOT_SELECTOR);

  if (root === null) {
    throw new Error(`Не найден корневой элемент ${ROOT_SELECTOR}`);
  }

  return root;
}

/** Название продукта: на всех языках одно, в словарь не переводится. */
const BRAND = 'CargoPASS';

interface MenuGroup {
  readonly title: string;
  /** Раздела нет только у «Профиля»: он не раздел фирмы (`Б9` блока `WEB-D.2`). */
  readonly items: ReadonlyArray<readonly [href: string, caption: string, section?: CompanySection]>;
}

/**
 * Группы брифа §2, подписи — прежние ключи экранов (`Б13`). Перечень разделов живёт
 * здесь литералами под типом контракта; совпадение с контрактом держит сторож `GQ36`.
 * Отбирает `buildMenu` по списку сервера, здесь условий нет.
 */
function menuGroups(companyId: string, strings: Strings): readonly MenuGroup[] {
  return [
    {
      title: strings.nav_group_works,
      items: [
        [worksPath(companyId), strings.works_title, 'works'],
        [worksToAssignPath(companyId), strings.works_to_assign_title, 'works_to_assign'],
      ],
    },
    {
      title: strings.nav_group_cargo,
      items: [
        [cargoFeedPath(companyId), strings.cargo_feed_title, 'cargo_feed'],
        [cargoListingsPath(companyId), strings.cargo_listings_title, 'cargo_listings'],
        [cargoOffersPath(companyId), strings.cargo_offers_mine_title, 'cargo_offers'],
      ],
    },
    {
      title: strings.nav_group_fleet,
      items: [
        [fleetPath(companyId), strings.fleet_title, 'fleet'],
        [driversPath(companyId), strings.drivers_title, 'drivers'],
        [vinClaimsPath(companyId), strings.vin_claims_title, 'vin_claims'],
      ],
    },
    {
      title: strings.nav_group_money,
      items: [
        [expensesPath(companyId), strings.expenses_title, 'expenses'],
        [payoutsPath(companyId), strings.payouts_title, 'payouts'],
      ],
    },
    {
      title: strings.nav_group_company,
      items: [
        [membersPath(companyId), strings.members_title, 'members'],
        [companyProfilePath(companyId), strings.company_profile_title, 'company_profile'],
        [profilePath(), strings.profile_title],
      ],
    },
  ];
}

function companyIdOf(route: Route): string | null {
  return 'companyId' in route ? route.companyId : null;
}

export function mountShell(doc: Document, options: ShellOptions): Shell {
  const { client } = options;
  const root = requireRoot(doc);

  const element = doc.createElement('div');
  const header = doc.createElement('header');
  const companySwitcher = doc.createElement('nav');
  const localeSwitcher = doc.createElement('nav');
  const slot = doc.createElement('main');
  const body = doc.createElement('div');
  const menu = doc.createElement('nav');
  const menuToggle = doc.createElement('button');
  const menuContent = doc.createElement('div');
  const signOutButton = doc.createElement('button');
  const signOutNotice = doc.createElement('p');

  element.className = 'shell';
  header.className = 'shell__header';
  companySwitcher.className = 'switcher switcher--company';
  localeSwitcher.className = 'switcher switcher--locale';
  slot.className = 'shell__route';

  body.className = 'shell__body';
  menu.className = 'shell__nav';
  menuToggle.className = 'shell__nav-toggle';
  menuToggle.type = 'button';
  menuContent.className = 'shell__nav-groups';
  menu.append(menuToggle, menuContent);
  signOutButton.className = 'shell__sign-out';
  signOutButton.type = 'button';
  signOutNotice.className = 'notice';
  signOutNotice.setAttribute('role', 'alert');

  header.append(companySwitcher, localeSwitcher);
  body.append(header, slot);
  element.append(body);
  root.replaceChildren(element);

  let strings: Strings = setLocale(options.locale ?? resolveLocale(navigator.languages));
  let profile: CurrentUserData | null = null;
  /**
   * Состояние оболочки: оно есть только тогда, когда не удалось действие самой
   * оболочки — чтение профиля или смена языка. Исходы экранов принадлежат
   * экранам, и второй раз здесь не разбираются.
   */
  let shellState: ScreenState | null = null;
  let mounted: MountedScreen | null = null;
  /**
   * Маршрут, стоящий на экране сейчас.
   *
   * Он нужен подписке на адрес: браузер шлёт событие на каждую запись адресной
   * строки, а перерисовывать место маршрута там, где место маршрута уже это и
   * показывает, значило бы спрашивать сервер во второй раз об одном и том же.
   */
  let shown: Route | null = null;
  /**
   * Поколение показа: им отменяется незавершённая загрузка.
   *
   * Растёт на каждой загрузке, и ответ прежнего поколения к экрану не
   * применяется — экрана этого на документе уже нет.
   */
  let generation = 0;

  /** Фирмы кабинета: членство с пустым списком разделов сюда не входит (`Б10`). */
  function memberships(): readonly Membership[] {
    return (profile?.memberships ?? []).filter((item) => item.available_sections.length > 0);
  }

  function sectionsOf(companyId: string): readonly CompanySection[] {
    return membershipsIn(memberships(), companyId).flatMap((item) => item.available_sections);
  }

  /**
   * Показать на домашней фирмы нечего (`Б11`): членство есть, раздела состава нет.
   * Чужая фирма сюда не входит: её адрес уходит на сервер, как раньше.
   */
  function homeHasNoMembers(companyId: string): boolean {
    return membershipsIn(profile?.memberships ?? [], companyId).length > 0 && !sectionsOf(companyId).includes('members');
  }

  function routeNow(): Route {
    // Неаутентифицированный адрес разрешается во вход: без токена спрашивать
    // сервер не о чем, и любой адрес кабинета ведёт туда же.
    return client.token === null ? { kind: 'login' } : currentRoute();
  }

  function applyLocale(locale: Locale): void {
    strings = setLocale(locale);
    doc.documentElement.lang = locale;
    client.useLocale(locale);
  }

  function renderCompanySwitcher(): void {
    if (memberships().length === 0) {
      // Переключать нечего: подпись без единого пункта обещает выбор, которого
      // нет, и первым её видит гость на экране входа.
      companySwitcher.replaceChildren();

      return;
    }

    const caption = doc.createElement('span');
    const active = companyIdOf(routeNow());

    caption.className = 'switcher__caption';
    caption.textContent = strings.company_switcher_label;

    const items: HTMLElement[] = [caption];

    // Порядок — тот, в котором членства вернул сервер. Статус не разбирается:
    // отбор по признаку участия был бы правилом, которое вычисляет сервер.
    for (const membership of memberships()) {
      const item = doc.createElement('button');

      item.type = 'button';
      item.className = 'switcher__item';
      item.textContent = membership.company.name;
      item.setAttribute('aria-current', String(membership.company.id === active));
      item.addEventListener('click', () => {
        selectCompany(membership.company.id);
      });

      items.push(item);
    }

    companySwitcher.replaceChildren(...items);
  }

  function renderLocaleSwitcher(): void {
    const caption = doc.createElement('span');

    caption.className = 'switcher__caption';
    caption.textContent = strings.language_label;

    const items: HTMLElement[] = [caption];

    for (const locale of SUPPORTED_LOCALES) {
      const item = doc.createElement('button');

      item.type = 'button';
      item.className = 'switcher__item';
      // Подпись — код языка: названий языков в наборах строк нет, а сочинить
      // их значило бы завести ключи, которых словарь блока не объявлял.
      item.textContent = locale;
      item.setAttribute('aria-current', String(locale === activeLocale()));
      item.addEventListener('click', () => {
        void selectLocale(locale);
      });

      items.push(item);
    }

    localeSwitcher.replaceChildren(...items);
  }

  /**
   * Вошедшему — кнопка, гостю — ничего. Отказ перерисовка не снимает: его снимают
   * новая попытка и переход на другой адрес, то есть действие человека.
   */
  function renderSignOut(): void {
    if (client.token === null) {
      signOutNotice.textContent = '';
      signOutButton.remove();
      signOutNotice.remove();
      signOutButton.disabled = false;

      return;
    }

    signOutButton.textContent = strings.nav_sign_out;

    if (signOutButton.parentElement !== header) {
      header.append(signOutButton, signOutNotice);
    }
  }

  /**
   * «Выйти» (`Б16`). Какой сеанс текущий, говорит только поле ответа. Без него
   * завершать нечего, и это отказ: токен и экран остаются. При успехе путь тот же,
   * что у завершения текущего сеанса в профиле; кнопка активна снова с экраном входа.
   */
  function leaveAfterSignOut(): void {
    // Токен отозван: вкладка забывает его сразу, а не по `401` следующего чтения.
    // Без токена чтения нет вовсе, и обрыв связи не может задержать экран входа.
    client.forgetToken();
    navigate({ kind: 'login' });
  }

  async function signOut(): Promise<void> {
    signOutButton.disabled = true;
    signOutNotice.textContent = '';

    try {
      const sessions = await client.listSessions();
      const current = sessions.find((session) => session.current);

      if (current === undefined) {
        signOutNotice.textContent = strings.state_error;
        signOutButton.disabled = false;

        return;
      }

      await client.terminateSession(current.id);
    } catch (error) {
      if (client.token === null) {
        // Токен отвергнут раньше выхода: конец сессии, а не отказ действия.
        profile = null;
        shellState = null;
        render();

        return;
      }

      signOutNotice.textContent = refusalText(error, strings);
      signOutButton.disabled = false;

      return;
    }

    leaveAfterSignOut();
  }

  signOutButton.addEventListener('click', () => {
    void signOut();
  });

  /** Для каких фирмы, языка и разделов собраны пункты: иначе узлы не трогаются и фокус не теряется. */
  let menuBuiltFor: string | null = null;

  function setMenuOpen(open: boolean): void {
    menu.dataset.open = String(open);
    menuToggle.setAttribute('aria-expanded', String(open));
  }

  menuToggle.addEventListener('click', () => {
    setMenuOpen(menuToggle.getAttribute('aria-expanded') !== 'true');
  });
  setMenuOpen(false);

  function buildMenu(companyId: string): void {
    const brand = doc.createElement('span');

    brand.className = 'shell__nav-brand';
    brand.textContent = BRAND;
    menu.setAttribute('aria-label', strings.nav_label);
    menuToggle.textContent = strings.nav_toggle;

    const sections = sectionsOf(companyId);
    // Группа без пунктов не показывается (`Б9`).
    const shownGroups = menuGroups(companyId, strings)
      .map((group) => ({
        title: group.title,
        items: group.items.filter(([, , section]) => section === undefined || sections.includes(section)),
      }))
      .filter((group) => group.items.length > 0);

    const groups = shownGroups.map((group) => {
      const node = doc.createElement('div');
      const title = doc.createElement('span');

      node.className = 'shell__nav-group';
      title.className = 'shell__nav-title';
      title.textContent = group.title;
      node.append(title);

      for (const [href, caption] of group.items) {
        const link = doc.createElement('a');

        link.className = 'shell__nav-item';
        link.href = href;
        link.setAttribute('href', href);
        link.textContent = caption;
        link.addEventListener('click', () => {
          setMenuOpen(false);
        });
        node.append(link);
      }

      return node;
    });

    menuContent.replaceChildren(brand, ...groups);
  }

  /** Текущий — пункт с самым длинным совпавшим префиксом адреса (`Б14`). */
  function markCurrentMenuItem(): void {
    const here = window.location.hash.split('?')[0] ?? '';
    const links = [...menuContent.querySelectorAll('a')];
    let current: HTMLAnchorElement | null = null;
    let matched = -1;

    for (const link of links) {
      const href = link.getAttribute('href') ?? '';

      if ((here === href || here.startsWith(`${href}/`)) && href.length > matched) {
        current = link;
        matched = href.length;
      }
    }

    for (const link of links) {
      if (link === current) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  function renderMenu(route: Route): void {
    const companyId = companyIdOf(route);

    if (companyId === null) {
      menu.remove();
      setMenuOpen(false);

      return;
    }

    const wanted = `${companyId}/${activeLocale()}/${sectionsOf(companyId).join()}`;

    if (wanted !== menuBuiltFor) {
      buildMenu(companyId);
      menuBuiltFor = wanted;
    }

    markCurrentMenuItem();

    if (menu.parentElement !== element) {
      element.prepend(menu);
    }
  }

  /** Перечень фирм как место входа: та же величина, что и в шапке. */
  function createCompanyChoiceScreen(): MountedScreen {
    const section = doc.createElement('section');
    const heading = doc.createElement('h1');
    const list = doc.createElement('ul');

    section.className = 'screen screen-companies';
    heading.textContent = strings.company_switcher_label;

    for (const membership of memberships()) {
      const row = doc.createElement('li');
      const link = doc.createElement('a');

      link.className = 'company-link';
      link.href = companyPath(membership.company.id);
      link.textContent = membership.company.name;
      row.append(link);
      list.append(row);
    }

    section.append(heading, list);

    return { element: section, load: null };
  }

  function createScreenFor(route: Route): MountedScreen {
    switch (route.kind) {
      case 'login': {
        const screen = createLoginScreen({
          client,
          strings,
          onAuthenticated: () => {
            // Профиль после входа читается заново: он и есть то, из чего
            // строится переключатель фирмы.
            profile = null;

            if (currentRoute().kind === 'login') {
              // **Адрес входа переживает собственный успех.** `#/login`
              // разбирается в маршрут `login` и при живом токене — условие
              // «токена нет» снято, а адрес, написанный по этому условию,
              // остался, и увести с него некому: форма входа стоит на экране
              // после удавшегося входа, и человек нажимает «Войти» второй раз.
              //
              // Адрес этот завела починка `Д-1` этого же блока: до неё
              // `#/login` не писал ни один путь, и потому болезнь появилась
              // вместе с починкой выхода. Уводит **переход**, а не перерисовка:
              // адрес — единственное место, где живёт маршрут (решение `D120`),
              // и перерисовка мимо него оставила бы адрес входа в адресной
              // строке. Довод и форма — те же, что у создания первой фирмы:
              // «оставлять человека на экране „фирм у вас нет“ значило бы
              // показывать неправду».
              //
              // Загрузку делает подписка на смену адреса — та же, что отвечает
              // кнопке «назад»: второй вызов рядом с переходом дал бы два
              // чтения профиля на один вход.
              navigate({ kind: 'companies' });

              return;
            }

            void load();
          },
        });

        return { element: screen.element, load: null };
      }

      case 'companies': {
        if (memberships().length > 0) {
          return createCompanyChoiceScreen();
        }

        const screen = createNoMembershipScreen({ client, strings });

        return { element: screen.element, load: null, relabel: screen.relabel };
      }

      case 'company': {
        // Членства профиля — экрану фирмы (находка стенда `С1` блока `WEB-2.1`):
        // член фирмы без права на состав видит заголовок, а отказ сервера — на
        // месте списка. Ссылки разделов — в меню каркаса (`Б2`, блок `WEB-D.1`).
        const screen = createCompanyScreen({
          client,
          strings,
          companyId: route.companyId,
          memberships: memberships(),
        });

        return { element: screen.element, load: screen.load };
      }

      // Работы фирмы из адреса — блок `WEB-2.2`: «все работы фирмы» и «ждут
      // назначения» — один экран, режим отбора сервера задаёт маршрут.
      case 'works':
      case 'worksToAssign': {
        // Сервер на чужую фирму отвечает пустой страницей (не раскрывает её), и
        // экран показал бы ложное «работ нет» (находка 1 стенда). Статус членства
        // клиент не разбирает: закрытое сервер отдаёт пустым `available_sections`,
        // и в `memberships()` его нет (`Б10`).
        if (profile === null) {
          return { element: renderScreenState('loading', strings), load: null };
        }

        if (!memberships().some((item) => item.company.id === route.companyId)) {
          return { element: renderScreenState('noAccess', strings), load: null };
        }

        const screen = createWorksScreen({
          client,
          strings,
          companyId: route.companyId,
          needsAssignment: route.kind === 'worksToAssign',
          sections: sectionsOf(route.companyId),
          onUnauthenticated: () => {
            profile = null;
            render();
          },
        });

        return { element: screen.element, load: screen.load };
      }

      // Форма внутреннего заказа (`WEB-3.5`, `Б2`): ворота те же, что у работ; раздел
      // здесь не проверяется, границу держит отказ сервера.
      case 'internalWorkNew': {
        if (profile === null) {
          return { element: renderScreenState('loading', strings), load: null };
        }

        if (!memberships().some((item) => item.company.id === route.companyId)) {
          return { element: renderScreenState('noAccess', strings), load: null };
        }

        const screen = createInternalWorkFormScreen({
          client,
          strings,
          companyId: route.companyId,
          onUnauthenticated: () => {
            profile = null;
            render();
          },
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'profile': {
        const screen = createProfileScreen({
          client,
          strings,
          onSignedOut: () => {
            // Токен отозван сервером немедленно: вкладке остаётся вход, и
            // ведёт туда маршрутизатор — второго пути смены экрана нет.
            leaveAfterSignOut();
          },
        });

        // Профиль переподписывается на месте (долг `TD-72`, ответ на ревью
        // `WEB-2.4` Р8): несохранённые дата рождения и пол переживают смену
        // языка.
        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'members': {
        const screen = createMembersScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      case 'memberRights': {
        const screen = createMemberRightsScreen({
          client,
          strings,
          companyId: route.companyId,
          membershipId: route.membershipId,
        });

        return { element: screen.element, load: screen.load };
      }

      case 'companyProfile': {
        const screen = createCompanyProfileScreen({
          client,
          strings,
          companyId: route.companyId,
        });

        return { element: screen.element, load: screen.load };
      }

      case 'memberDocuments': {
        const screen = createMemberDocumentsScreen({
          client,
          strings,
          companyId: route.companyId,
          membershipId: route.membershipId,
        });

        return { element: screen.element, load: screen.load };
      }

      // Экраны домена транспорта — блок `WEB-1.3`, по форме соседей.
      case 'fleet': {
        const screen = createFleetScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      case 'vehicleService': {
        const screen = createVehicleServiceScreen({
          client,
          strings,
          companyId: route.companyId,
          vehicleId: route.vehicleId,
        });

        // Форма записи со строками запчастей — переподписывается на месте
        // (долг `TD-72`, блок `WEB-2.4`): введённое переживает смену языка.
        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'vinClaims': {
        const screen = createVinClaimsScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      // Экраны кабинета логиста — блок `WEB-2.1`, по форме соседей. Форма
      // объявления переподписывается при смене языка, а не пересобирается
      // (долг `TD-72`).
      case 'cargoListings': {
        const screen = createCargoListingsScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      case 'cargoListingNew': {
        const screen = createCargoListingFormScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'cargoListing': {
        const screen = createCargoListingScreen({
          client,
          strings,
          companyId: route.companyId,
          listingId: route.listingId,
        });

        return { element: screen.element, load: screen.load };
      }

      case 'cargoListingEdit': {
        const screen = createCargoListingFormScreen({
          client,
          strings,
          companyId: route.companyId,
          listingId: route.listingId,
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      // Экраны кабинета диспетчера — блок `WEB-2.2`, по форме соседей. Экраны с
      // вводом переподписываются при смене языка (долг `TD-72`).
      case 'cargoFeed': {
        const screen = createCargoFeedScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'cargoFeedListing': {
        const screen = createCargoFeedListingScreen({
          client,
          strings,
          companyId: route.companyId,
          listingId: route.listingId,
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'cargoOffers': {
        const screen = createCargoOffersScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      case 'workAssignment': {
        const screen = createWorkAssignmentScreen({
          client,
          strings,
          companyId: route.companyId,
          workId: route.workId,
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      // Карточка работы — блок `WEB-3.1`. Единственный экран трека с таймером:
      // снимаясь, оболочка гасит его через `dispose`.
      case 'workCard': {
        const screen = createWorkCardScreen({
          client,
          strings,
          companyId: route.companyId,
          workId: route.workId,
          onUnauthenticated: () => {
            profile = null;
            render();
          },
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel, dispose: screen.dispose };
      }

      // Экраны кабинета механика — блок `WEB-2.4`, по форме соседей. Карточка
      // несёт формы неисправности и фото и переподписывается при смене языка:
      // введённый комментарий и выбранные файлы живут в самих полях.
      case 'vehicle': {
        const screen = createVehicleCardScreen({
          client,
          strings,
          companyId: route.companyId,
          vehicleId: route.vehicleId,
        });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'drivers': {
        const screen = createCompanyDriversScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load };
      }

      // Экраны кабинета бухгалтера — блок `WEB-2.3`, по форме соседей. Форма
      // записи и правки на экране списка переподписывается при смене языка:
      // введённое и выбранный чек живут в самих полях (долг `TD-72`).
      case 'expenses': {
        const screen = createExpensesScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'payouts': {
        const screen = createPayoutsScreen({ client, strings, companyId: route.companyId });

        return { element: screen.element, load: screen.load, relabel: screen.relabel };
      }

      case 'notFound':
        return { element: createNotFoundScreen(strings).element, load: null };
    }
  }

  function render(): void {
    const route = routeNow();

    shown = route;
    renderMenu(route);
    renderCompanySwitcher();
    renderLocaleSwitcher();
    renderSignOut();

    // Снимаемый экран гасит таймеры и больше не применяет ответы (`TD-63`).
    mounted?.dispose?.();

    if (shellState !== null) {
      mounted = null;
      slot.replaceChildren(renderScreenState(shellState, strings));

      return;
    }

    mounted = createScreenFor(route);
    slot.replaceChildren(mounted.element);
  }

  /**
   * Отмена незавершённой загрузки — **единственное** место отмены (долг `TD-39`).
   *
   * Переход, случившийся до возврата чтения профиля или загрузки экрана,
   * оставлял прежнюю загрузку доигрывать: ответ приходил и применялся к
   * экрану, снятому с документа. Пока кабинет только читал, повтор был
   * безвреден; блок приносит семь операций, меняющих состояние, и день
   * возврата настал.
   *
   * Механизм — поколение показа. Второго имени и второго флага рядом быть не
   * должно: часть переходов отменялась бы одним, часть другим, и разошлись бы
   * они молча.
   */
  /** Смену адреса вызвал уход с домашней: профиль этого перехода уже прочитан. */
  let leavingHome = false;

  function cancelPendingRender(): void {
    generation += 1;
  }

  async function load(): Promise<void> {
    cancelPendingRender();

    const mine = generation;

    if (client.token === null) {
      shellState = null;
      render();

      return;
    }

    if (profile === null) {
      try {
        const received = await client.getCurrentUser();

        if (mine !== generation) {
          return;
        }

        profile = received;
        // Язык вошедшего определяет профиль: так говорит контракт (`D85` —
        // «язык ответов владельца токена определяется профилем»), и так
        // записано решением `D122` — источник истины после входа — ответ
        // сервера. Оставить интерфейс на языке браузера значило бы показывать
        // сообщения сервера на одном языке, а подписи вокруг них — на другом,
        // и человек видел бы два языка в одном экране.
        applyLocale(profile.locale);
        shellState = null;
      } catch (error) {
        if (mine !== generation) {
          return;
        }

        if (client.token === null) {
          // Токен отвергнут сервером, и слой его уже забыл — так он отвечает
          // на `UNAUTHENTICATED`. Это **не отказ экрана**, а конец сессии:
          // вкладке остаётся вход, и разрешает туда `routeNow()` по тому же
          // пустому токену. Состояние отказа здесь было бы мёртвым экраном —
          // место маршрута держало бы подпись «данные получить не удалось», а
          // снять её нечем: следующей загрузки не будет, потому что адрес уже
          // тот, что нужен. Ровно так выглядел выход из своей текущей сессии
          // до этой правки. Ветка та же, что у ленты работ: `UNAUTHENTICATED`
          // возвращает на вход, а не красит экран.
          profile = null;
          shellState = null;
          render();

          return;
        }

        shellState = screenStateFor({
          kind: 'failed',
          code: error instanceof ApiFailure ? error.code : null,
        });
        render();

        return;
      }
    }

    // Домашняя без раздела состава (`Б11`): запрос ответил бы `403`. Запись истории
    // заменяется, иначе «назад» возвращал бы сюда и уводил снова. Экран рисует
    // подписка на смену адреса, как при всяком переходе; профиль только что прочитан,
    // и перечитывать его она не станет.
    const home = routeNow();

    if (home.kind === 'company' && homeHasNoMembers(home.companyId)) {
      leavingHome = true;
      replaceRoute({ kind: 'works', companyId: home.companyId });

      return;
    }

    render();

    if (mounted !== null && mounted.load !== null) {
      await mounted.load();
    }
  }

  function selectCompany(companyId: string): void {
    // Переход идёт через маршрутизатор и **только** через него: адрес —
    // единственное место, где живёт выбранная фирма (решение `D120`), и запись
    // мимо него разошлась бы с подписчиками. Перерисовку делает подписка на
    // смену адреса — та же, что отвечает кнопке «назад» и набранной руками
    // ссылке: перерисовка рядом с переходом была бы вторым путём, по которому
    // экран меняется, и он расходился бы с первым молча.
    navigate({ kind: 'company', companyId });
  }

  async function selectLocale(locale: Locale): Promise<void> {
    if (client.token === null) {
      // Гость: выбор живёт время вкладки и уходит заголовком языка на
      // обращениях без токена. Хранилища у выбора нет — решение `D28`.
      applyLocale(locale);
      shellState = null;
      render();

      return;
    }

    try {
      const updated = await client.updateCurrentUser({ locale });

      // Источник истины — ответ сервера: клиент, поверивший себе, разойдётся с
      // профилем молча, и человек увидит язык, которого в профиле нет.
      applyLocale(updated.locale);
      shellState = null;
    } catch (error) {
      shellState = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
      render();

      return;
    }

    // Долг `TD-65`, поручение владельца. Голым `render()` здесь кончать
    // нельзя: перерисовка **монтирует** экран заново — узел на новом языке
    // строится с нуля, — а прочитать свои данные ему нечем, потому что
    // `mounted.load()` зовёт только собственная загрузка оболочки. Экран
    // вставал в начальное «Загрузка…» и оставался в нём навсегда, не послав
    // после `PATCH /v1/me` ни одного обращения. Хуже отказа: он **выглядит**
    // работающим.
    //
    // Зовётся не `mounted.load()` рядом, а `load()` оболочки целиком — и
    // причина в отмене. Отмена незавершённой загрузки обязана остаться
    // **единственным** механизмом (долг `TD-39`), а `load()` начинается с
    // `cancelPendingRender()`: чтение, начатое до смены языка, к новому экрану
    // не применится. Собственный вызов загрузки рядом был бы вторым путём,
    // мимо отмены, и разошёлся бы с первым молча — ровно то, чего долг
    // запрещает. Второго чтения профиля это не стоит: он уже прочитан, и
    // `load()` спрашивает сервер только когда профиля нет.
    //
    // Исключение одно — экран с формой ввода (долг `TD-72`, блок `WEB-2.1`).
    // Пересборка монтирует узел из пустого шаблона, и введённое в форму
    // пропадает: на форме создания фирмы три поля обнулялись, и отправка уходила
    // пустой. Такой экран переподписывается на месте. Второго пути загрузки это
    // не заводит: экран ничего не читает заново, и отменять нечего — механизм
    // отмены остаётся единственным.
    if (mounted !== null && mounted.relabel !== undefined) {
      renderMenu(routeNow());
      renderCompanySwitcher();
      renderLocaleSwitcher();
      renderSignOut();
      mounted.relabel(strings);

      return;
    }

    await load();
  }

  /** Тот же маршрут, что на экране: `kind`, фирма, запись членства, запись
   * реестра транспорта, объявление о грузе и работа — всё, чем маршруты
   * разнятся. */
  function alreadyShown(route: Route): boolean {
    return (
      shown !== null
      && shown.kind === route.kind
      && companyIdOf(shown) === companyIdOf(route)
      && membershipIdOf(shown) === membershipIdOf(route)
      && vehicleIdOf(shown) === vehicleIdOf(route)
      && listingIdOf(shown) === listingIdOf(route)
      && workIdOf(shown) === workIdOf(route)
    );
  }

  /**
   * Смена адреса — единственный сигнал о переходе, и оболочка слушает его сама.
   *
   * Без подписки переход состоится в адресе и не состоится на экране: место
   * маршрута останется с прежним экраном, а шапка — с прежним списком фирм.
   *
   * Профиль при этом перечитывается, а не берётся из памяти. Список членств
   * оболочка получает на входе, а адрес меняется действием человека — и первое
   * же такое действие продукта, создание первой фирмы, этот список меняет.
   * Дописать созданное в список здесь нечем: пункт, которого сервер не
   * подтверждал, — второй источник членств, спорящий с профилем молча (`D129`).
   *
   * Два условия молчания названы прямо. Маршрут, который уже на экране, второй
   * раз не рисуется — иначе одна запись адресной строки стоила бы лишнего
   * обращения к серверу. Оболочка, снятая с документа, молчит вовсе: подписка
   * живёт столько же, сколько страница, и снятое дерево, продолжающее ходить в
   * сеть, — утечка, а не поведение. **Второе условие не покрыто ни одним
   * тестом, и снять его нельзя**: маршрутизатор отписки не даёт, перечень
   * обработчиков только растёт, и именно эта строка держит устойчивость всего
   * набора. Она выглядит мёртвой и мёртвой не является — сказано здесь, чтобы
   * следующий, кто придёт убирать лишнее, прочитал раньше, чем удалит.
   *
   * **Цена решения и поправка к прежней её формулировке.** Переключение фирмы
   * стоит лишнего чтения профиля: перечитывается он на любой смене маршрута, а
   * не только после создания. В сообщении коммита эта цена была объяснена так,
   * будто иного пути нет вовсе — «разделить можно только правилом в клиенте».
   * **Это было сказано слишком сильно, и находка ревью дельты права.** Третий
   * путь есть, и его прецедент стоит двумя функциями выше в этом же файле:
   * `onAuthenticated` делает ровно `profile = null; void load()`, и экран
   * создания фирмы мог бы получить такое же уведомление. Правилом в клиенте
   * это не является: клиент знал бы не о предметной области, а о собственной
   * записи. Выбор при этом **не меняется**, и причина другая, чем прежняя:
   * перечень мест, обязанных уведомить, однажды окажется неполным молча — а
   * перечитывание на каждой смене адреса отказывает в безопасную сторону.
   * Именно неполнота такого перечня и была тем дефектом, который нашёл руками
   * шаг СТЕНД.
   */
  onRouteChange(() => {
    // Маршрут берётся своим разрешением, а не приходящим: без токена оболочка
    // стоит на входе при любом адресе, и разобранный маршрут кабинета сменил бы
    // экран входа посреди набранного кода.
    const profileIsFresh = leavingHome;

    leavingHome = false;

    if (!element.isConnected || alreadyShown(routeNow())) {
      return;
    }

    setMenuOpen(false);
    signOutNotice.textContent = '';

    if (!profileIsFresh) {
      profile = null;
    }

    void load();
  });

  applyLocale(activeLocale());
  render();

  return {
    element,
    header,

    get route(): Route {
      return routeNow();
    },

    get locale(): Locale {
      return activeLocale();
    },

    get activeCompanyId(): string | null {
      return companyIdOf(routeNow());
    },

    get companyIds(): readonly string[] {
      const found: string[] = [];

      for (const membership of memberships()) {
        found.push(membership.company.id);
      }

      return found;
    },

    load,
    selectCompany,
    selectLocale,
  };
}
